import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useNavigation, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { guardedPush } from "@src/services/navigation/guarded-push";
import { useUsersAndPermissionsData } from "@src/features/settings/hooks";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useCameras } from "@src/features/cameras";
import { resolveCameraName } from "@src/features/cameras/utils/camera-display-name";
import {
  useIsTenantAdmin,
  useUserList,
  type UserListFilters,
  type UserListFilterSite,
  type UserListFilterCamera,
  type UserListItem,
} from "@visioni/a3s-access/app";

export function useUsersAndPermissionsPage() {
  const { theme } = useUnistyles();
  const navigation = useNavigation();
  const router = useRouter();
  const { headerLeft } = useHeaderBackButton();

  const { isTenantAdmin, isLoading: permissionsLoading, isError: permissionsError } = useIsTenantAdmin();
  const { isReady, isError } = useUsersAndPermissionsData();
  const { users } = useUserList();
  const { sites } = useSites();
  const { data: cameras } = useCameras();

  const [filters, setFilters] = useState<UserListFilters>({ search: "", role: null, siteId: null, cameraId: null });

  const filterSites: UserListFilterSite[] = useMemo(
    () => (sites ?? []).map((s) => ({ id: String(s.siteId), name: s.siteName })),
    [sites],
  );

  const filterCameras: UserListFilterCamera[] = useMemo(
    () =>
      (cameras ?? []).map((c) => {
        const cameraKey = `${c.key.asset}@${c.key.id}`;
        return {
          id: `${c.site.id}/${cameraKey}`,
          name: resolveCameraName(c, cameraKey),
        };
      }),
    [cameras],
  );

  const filteredUsers: UserListItem[] = useMemo(() => {
    let result = users;

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((u) => u.name.toLowerCase().includes(q) || (u.email?.toLowerCase().includes(q) ?? false));
    }

    if (filters.role) {
      result = result.filter((u) => u.permissions.some((p) => p.role === filters.role));
    }

    if (filters.siteId) {
      const siteId = filters.siteId;
      result = result.filter(
        (u) => u.permissions.some((p) => p.role === "tenant_admin") || u.permissions.some((p) => p.siteId === siteId),
      );
    }

    if (filters.cameraId) {
      const cameraId = filters.cameraId;
      const cameraSiteId = cameraId.includes("/") ? cameraId.substring(0, cameraId.indexOf("/")) : undefined;
      result = result.filter(
        (u) =>
          u.permissions.some((p) => p.role === "tenant_admin") ||
          u.permissions.some((p) => p.cameraId === cameraId) ||
          (cameraSiteId &&
            u.permissions.some(
              (p) => p.siteId === cameraSiteId && (p.role === "site_manager" || p.role === "site_viewer"),
            )),
      );
    }

    return result;
  }, [users, filters]);

  const handleInviteUser = useCallback(() => {
    guardedPush("/settings/site-access/invite-user");
  }, []);

  const handleEditUser = useCallback((userId: string) => {
    guardedPush(`/settings/site-access/edit-user/${userId}` as never);
  }, []);

  useLayoutEffect(() => {
    if (!permissionsLoading && !permissionsError && !isTenantAdmin) {
      router.replace("/forbidden");
    }
  }, [permissionsLoading, permissionsError, isTenantAdmin, router]);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Users & Permissions",
      headerLeft,
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
    });
  }, [navigation, theme.colors.background, headerLeft]);

  return {
    permissionsLoading,
    permissionsError,
    isTenantAdmin,
    isReady,
    isError,
    filters,
    setFilters,
    filterSites,
    filterCameras,
    filteredUsers,
    handleInviteUser,
    handleEditUser,
  };
}
